class BlemProductDrawers {
  constructor(root) {
    this.root = root;
    if (root.dataset.blemDrawersReady === 'true') return;
    root.dataset.blemDrawersReady = 'true';
    this.openers = root.querySelectorAll('[data-blem-drawer-open]');
    this.drawers = root.querySelectorAll('.blem-product-drawer');
    this.bindEvents();
    this.initializeSizeGuide();
    this.initializeProductVideo();
    this.initializeMobilePurchase();
  }

  initializeSizeGuide() {
    this.root.querySelectorAll('[data-blem-size-guide]').forEach((guide) => {
      const buttons = guide.querySelectorAll('[data-blem-size-unit]');
      const cells = guide.querySelectorAll('[data-in][data-cm]');

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const unit = button.dataset.blemSizeUnit;
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          cells.forEach((cell) => {
            cell.textContent = cell.dataset[unit] || cell.textContent;
          });
        });
      });
    });
  }

  initializeProductVideo() {
    const videos = this.root.querySelectorAll('.product__video-column video');
    if (!videos.length) return;

    const playVideos = () => {
      videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.autoplay = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        const playAttempt = video.play();
        if (playAttempt?.catch) playAttempt.catch(() => {});
      });
    };

    playVideos();
    videos.forEach((video) => video.addEventListener('canplay', playVideos, { once: true }));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) playVideos();
    });
    document.addEventListener('touchstart', playVideos, { once: true, passive: true });
  }

  bindEvents() {
    this.openers.forEach((opener) => {
      opener.addEventListener('click', () => {
        const drawer = document.getElementById(opener.dataset.blemDrawerOpen);
        if (!drawer) return;
        drawer.showModal();
        document.documentElement.classList.add('blem-drawer-open');
      });
    });

    this.drawers.forEach((drawer) => {
      drawer.querySelector('[data-blem-drawer-close]')?.addEventListener('click', () => drawer.close());
      drawer.addEventListener('click', (event) => {
        if (event.target === drawer) drawer.close();
      });
      drawer.addEventListener('close', () => document.documentElement.classList.remove('blem-drawer-open'));
    });
  }

  initializeMobilePurchase() {
    const bar = this.root.querySelector('[data-blem-mobile-purchase]');
    const mainAdd = this.root.querySelector('.product-form__submit');
    if (!bar || !mainAdd) return;

    const add = bar.querySelector('[data-blem-mobile-add]');
    const sizeOpen = bar.querySelector('[data-blem-mobile-size-open]');
    const sizeLabel = bar.querySelector('[data-blem-mobile-size-label]');
    const sizeDrawer = this.root.querySelector('[data-blem-mobile-size-drawer]');
    const sizeOptions = this.root.querySelector('[data-blem-mobile-size-options]');
    const sizeClose = this.root.querySelector('[data-blem-mobile-size-close]');
    const productInformation = this.root.querySelector('[id^="ProductInfo-"]');
    const mobilePrice = bar.querySelector('[data-blem-mobile-price]');
    const mainPrice = this.root.querySelector('[id^="price-"]');
    const getSizeFieldset = () =>
      [...this.root.querySelectorAll('variant-selects fieldset')].find((fieldset) =>
        /size|taille/i.test(fieldset.querySelector('legend')?.textContent || '')
      );
    const selectSizeText = this.root.dataset.blemSelectSize || 'Select size';
    const sizeLabelText = this.root.dataset.blemSizeLabel || 'Size [size]';
    const addToCartText = this.root.dataset.blemAddToCart || 'Add to cart';

    const syncSize = () => {
      const sizeFieldset = getSizeFieldset();
      const selected = sizeFieldset?.querySelector('input:checked');
      if (sizeLabel) sizeLabel.textContent = selected ? sizeLabelText.replace('[size]', selected.value) : selectSizeText;
      sizeOptions?.querySelectorAll('.blem-mobile-size-option').forEach((option) => {
        const currentInput = sizeFieldset?.querySelector(`#${CSS.escape(option.dataset.inputId)}`);
        const isSelected = currentInput?.checked === true;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        option.disabled = !currentInput || currentInput.disabled || currentInput.classList.contains('disabled');
      });
      if (add) {
        add.disabled = mainAdd.disabled;
        add.textContent = mainAdd.querySelector('span')?.textContent.trim() || addToCartText;
      }
      if (mobilePrice && mainPrice) {
        const salePrice = mainPrice.querySelector('.price-item--sale');
        const regularPrice = mainPrice.querySelector('.price-item--regular');
        const displayedPrice = salePrice?.textContent.trim() || regularPrice?.textContent.trim();
        if (displayedPrice) mobilePrice.textContent = displayedPrice;
      }
    };

    const initialSizeFieldset = getSizeFieldset();
    if (sizeOpen && sizeDrawer && sizeOptions && initialSizeFieldset) {
      [...initialSizeFieldset.querySelectorAll('input[type="radio"]')].forEach((input) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'blem-mobile-size-option';
        option.textContent = input.value;
        option.dataset.value = input.value;
        option.dataset.inputId = input.id;
        option.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
        option.disabled = input.disabled || input.classList.contains('disabled');
        option.addEventListener('click', () => {
          const currentSizeFieldset = getSizeFieldset();
          const currentInput = currentSizeFieldset?.querySelector(`#${CSS.escape(option.dataset.inputId)}`);
          if (!currentInput || currentInput.disabled || currentInput.classList.contains('disabled')) return;

          if (!currentInput.checked) {
            currentInput.checked = true;
            currentInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, detail: { blemPreventFocus: true } })
            );
            syncSize();
            if (add) add.disabled = true;
          } else {
            syncSize();
          }
          sizeDrawer.close();
        });
        sizeOptions.appendChild(option);
      });
      sizeOpen.addEventListener('click', () => sizeDrawer.showModal());
      sizeClose?.addEventListener('click', () => sizeDrawer.close());
      sizeDrawer.addEventListener('click', (event) => {
        if (event.target === sizeDrawer) sizeDrawer.close();
      });
      this.root.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects fieldset') === getSizeFieldset()) syncSize();
      });
    } else {
      sizeOpen?.remove();
    }

    const updatePosition = () => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const informationRect = productInformation?.getBoundingClientRect();
      const informationIsVisible = informationRect
        ? informationRect.top < window.innerHeight && informationRect.bottom > 0
        : false;
      const hasPassedProductInformation = informationRect ? informationRect.bottom <= 0 : false;

      bar.hidden = informationIsVisible;
      bar.classList.toggle('blem-mobile-purchase--top', hasPassedProductInformation);
      bar.classList.toggle('blem-mobile-purchase--bottom', !hasPassedProductInformation);
    };

    const buttonObserver = new MutationObserver(syncSize);
    buttonObserver.observe(mainAdd, { attributes: true, childList: true, subtree: true });
    if (mainPrice) buttonObserver.observe(mainPrice, { childList: true, subtree: true });
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    syncSize();
    updatePosition();
  }
}

const initializeBlemProductDrawers = (scope = document) => {
  scope.querySelectorAll('product-info').forEach((root) => new BlemProductDrawers(root));
};

initializeBlemProductDrawers();
document.addEventListener('shopify:section:load', (event) => initializeBlemProductDrawers(event.target));
